import { supabase } from "../../supabase/supabase";

// A disposition is what a call concluded. Each assistant has its own list, stored on the assistant
// (assistants.dispositions, voxi-2-0#243); after each call voxi-2-0 picks one from the transcript.

export interface Disposition {
  key: string;
  label: string;
  description: string;
}

// The rules the database and voxi-2-0 also hold: keys are lowercase snake_case, at most 40
// characters, unique in the list, and `other` is reserved (Voxi always adds it). At most 15.
export const DISPOSITION_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const RESERVED_DISPOSITION_KEYS = ["other"];
export const MAX_ASSISTANT_DISPOSITIONS = 15;

/** A key suggestion from a label: "Not interested!" becomes "not_interested". */
export function suggestDispositionKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[0-9_]+/, "")
    .slice(0, 40)
    .replace(/_+$/, "");
}

/** The first problem with a disposition, or null when it can be saved. */
export function checkDisposition(input: Disposition, takenKeys: string[]): string | null {
  if (!input.label.trim()) return "Add a label.";
  if (!DISPOSITION_KEY_PATTERN.test(input.key)) {
    return "The key must be lowercase letters, digits and underscores, start with a letter, and be at most 40 characters.";
  }
  if (RESERVED_DISPOSITION_KEYS.includes(input.key)) {
    return `"${input.key}" is reserved, Voxi adds it itself.`;
  }
  if (takenKeys.includes(input.key)) return `The key "${input.key}" is already in this list.`;
  if (!input.description.trim()) {
    return "Add a description. The model reads it to decide when to pick this disposition.";
  }
  return null;
}

/**
 * An assistant's dispositions as stored: the valid items (trimmed, keys unique), at most
 * MAX_ASSISTANT_DISPOSITIONS, or null when there are none. Anything else reads as null.
 */
export function checkDispositions(value: unknown): Disposition[] | null {
  if (!Array.isArray(value)) return null;
  const out: Disposition[] = [];
  for (const item of value) {
    if (out.length >= MAX_ASSISTANT_DISPOSITIONS) break;
    if (!item || typeof item !== "object") continue;
    const { key, label, description } = item as Record<string, unknown>;
    if (typeof key !== "string" || typeof label !== "string" || typeof description !== "string") {
      continue;
    }
    const d = { key: key.trim(), label: label.trim(), description: description.trim() };
    if (checkDisposition(d, out.map((x) => x.key)) === null) out.push(d);
  }
  return out.length > 0 ? out : null;
}

export const dispositionService = {
  /**
   * Up to 10 dispositions suggested from the agent's prompt (edge function suggest_dispositions),
   * none repeating a key of `current`. Nothing is saved. Throws with the function's own message on
   * failure.
   */
  async suggestDispositions(prompt: string, current: Disposition[]): Promise<Disposition[]> {
    const { data, error } = await supabase.functions.invoke("suggest_dispositions", {
      body: { prompt, existing: current },
    });
    if (error) {
      // A non-2xx answer carries the function's own { error } message in the response body.
      let message: string | undefined;
      try {
        const body = await (error as any).context?.json?.();
        message = body?.error;
      } catch {
        // keep the generic message
      }
      throw new Error(message || "Could not suggest dispositions.");
    }
    return (data as { suggestions?: Disposition[] })?.suggestions ?? [];
  },
};
